/* eslint-disable */

import { jsPDF } from "jspdf";

const PAGE_WIDTH_MM = 80;
const MEASUREMENT_PAGE_HEIGHT_MM = 5000;
const MARGIN_X_MM = 4;
const MARGIN_TOP_MM = 5;
const MARGIN_BOTTOM_MM = 5;
const FINAL_PAPER_FEED_MM = 16.5;
const PAPER_FEED_ANCHOR_RADIUS_MM = 0.15;
const CONTENT_WIDTH_MM = 72;
const TRAGA_LABEL_X = MARGIN_X_MM;
const TRAGA_ENTRY_RIGHT_X = MARGIN_X_MM + 41;
const TRAGA_EXIT_RIGHT_X = MARGIN_X_MM + CONTENT_WIDTH_MM;
const TRAGA_HEADER_FONT_SIZE = 8.6;
const TRAGA_VALUE_FONT_SIZE = 10;
const MACHINE_VALUE_FONT_SIZE = 10;
const PELUCHERA_VALUE_FONT_SIZE = 9.6;
const GROUP_TITLE_FONT_SIZE = 11.5;
const GROUP_TITLE_MIN_FONT_SIZE = 9.4;
const TICKET_HEADER_TITLE_FONT_SIZE = 12.5;
const TICKET_HEADER_DATE_FONT_SIZE = 11.8;
const TYPE_TOTAL_FONT_SIZE = 12;
const GENERAL_TOTAL_FONT_SIZE = 13;
const GENERAL_SPLIT_TOTAL_FONT_SIZE = 11.8;
const FONT_FAMILY = "helvetica";

const sanitizeFilename = (name) =>
  String(name ?? "recaudacion-ticket")
    .replace(/[\/\\:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const makeDoc = (pageHeight = MEASUREMENT_PAGE_HEIGHT_MM) =>
  new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [PAGE_WIDTH_MM, pageHeight],
    compress: false,
  });

const lineHeightForFont = (fontSize) => Math.max(3.2, fontSize * 0.5);

const drawWrappedText = (doc, text, x, y, options = {}) => {
  const {
    fontSize = 8,
    fontStyle = "normal",
    align = "left",
    maxWidth = CONTENT_WIDTH_MM,
  } = options;

  doc.setFont(FONT_FAMILY, fontStyle);
  doc.setFontSize(fontSize);

  const lines = doc.splitTextToSize(String(text ?? ""), maxWidth);
  const lineHeight = lineHeightForFont(fontSize);

  lines.forEach((line, index) => {
    doc.text(line, x, y + index * lineHeight, { align, baseline: "top" });
  });

  return {
    lineCount: lines.length,
    height: lines.length * lineHeight,
  };
};

const ensurePage = (doc, yRef, requiredHeight = 4) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (yRef.value + requiredHeight <= pageHeight - MARGIN_BOTTOM_MM) return;
  doc.addPage([PAGE_WIDTH_MM, pageHeight], "p");
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_WIDTH_MM, pageHeight, "F");
  yRef.value = MARGIN_TOP_MM;
};

const writeLine = (doc, yRef, text, options = {}) => {
  const fontSize = options.fontSize ?? 8;
  const spacingBefore = options.spacingBefore ?? 0;
  const spacingAfter = options.spacingAfter ?? 0.4;
  const maxWidth = options.maxWidth ?? CONTENT_WIDTH_MM;

  doc.setFont(FONT_FAMILY, options.fontStyle ?? "normal");
  doc.setFontSize(fontSize);
  const lineCount = doc.splitTextToSize(String(text ?? ""), maxWidth).length;
  const requiredHeight = spacingBefore + lineCount * lineHeightForFont(fontSize);

  yRef.value += spacingBefore;
  ensurePage(doc, yRef, requiredHeight);

  const { height } = drawWrappedText(doc, text, MARGIN_X_MM, yRef.value, {
    ...options,
    fontSize,
    maxWidth,
  });
  yRef.value += height + spacingAfter;
};

const writeFittedLine = (doc, yRef, text, options = {}) => {
  const fontSize = options.fontSize ?? 8;
  const minFontSize = options.minFontSize ?? fontSize;
  const fontStyle = options.fontStyle ?? "normal";
  const spacingBefore = options.spacingBefore ?? 0;
  const spacingAfter = options.spacingAfter ?? 0.4;
  const maxWidth = options.maxWidth ?? CONTENT_WIDTH_MM;
  const align = options.align ?? "left";
  const value = String(text ?? "");

  doc.setFont(FONT_FAMILY, fontStyle);
  doc.setFontSize(fontSize);

  const naturalWidth = doc.getTextWidth(value);
  const fittedFontSize =
    naturalWidth > maxWidth
      ? Math.max(minFontSize, fontSize * (maxWidth / naturalWidth))
      : fontSize;

  doc.setFontSize(fittedFontSize);
  const fittedWidth = doc.getTextWidth(value);
  const horizontalScale = fittedWidth > maxWidth ? maxWidth / fittedWidth : 1;
  const shouldWrap = options.wrapAtMin === true && horizontalScale < 0.8;
  const wrappedLines = shouldWrap ? doc.splitTextToSize(value, maxWidth) : [value];
  const requiredHeight =
    spacingBefore + wrappedLines.length * lineHeightForFont(fittedFontSize);

  yRef.value += spacingBefore;
  ensurePage(doc, yRef, requiredHeight);
  const x = align === "center" ? MARGIN_X_MM + CONTENT_WIDTH_MM / 2 : MARGIN_X_MM;
  wrappedLines.forEach((line, index) => {
    doc.text(line, x, yRef.value + index * lineHeightForFont(fittedFontSize), {
      align,
      baseline: "top",
      horizontalScale: shouldWrap ? 1 : horizontalScale,
    });
  });
  yRef.value += wrappedLines.length * lineHeightForFont(fittedFontSize) + spacingAfter;
};

const splitKeepingMoneyTogether = (doc, text, maxWidth) => {
  const tokens = String(text ?? "").match(/\$\s+\S+|\S+/g) || [""];
  const lines = [];
  let currentLine = "";

  tokens.forEach((token) => {
    const candidate = currentLine ? `${currentLine} ${token}` : token;
    if (currentLine && doc.getTextWidth(candidate) > maxWidth) {
      lines.push(currentLine);
      currentLine = token;
    } else {
      currentLine = candidate;
    }
  });

  if (currentLine || lines.length === 0) lines.push(currentLine);
  return lines;
};

const writePelucheraValueLine = (doc, yRef, text, options = {}) => {
  const fontSize = options.fontSize ?? PELUCHERA_VALUE_FONT_SIZE;
  const spacingAfter = options.spacingAfter ?? 0.4;
  const maxWidth = options.maxWidth ?? CONTENT_WIDTH_MM;
  const value = String(text ?? "");

  doc.setFont(FONT_FAMILY, options.fontStyle ?? "normal");
  doc.setFontSize(fontSize);

  const lines =
    doc.getTextWidth(value) <= maxWidth
      ? [value]
      : value
          .split(/\s+\|\s+/)
          .flatMap((block) => splitKeepingMoneyTogether(doc, block, maxWidth));

  lines.forEach((line) => {
    ensurePage(doc, yRef, lineHeightForFont(fontSize));
    doc.text(line, MARGIN_X_MM, yRef.value, { align: "left", baseline: "top" });
    yRef.value += lineHeightForFont(fontSize);
  });
  yRef.value += spacingAfter;
};

const writePairLine = (doc, yRef, label, value, options = {}) => {
  const text = value == null || value === "" ? `${label} -` : `${label} ${value}`;
  writeLine(doc, yRef, text, options);
};

const drawDividerLine = (doc, yRef, options = {}) => {
  const spacingBefore = options.spacingBefore ?? 0.4;
  const spacingAfter = options.spacingAfter ?? 0.5;
  const lineWidth = options.lineWidth ?? 0.2;
  const requiredHeight = spacingBefore + 0.2;

  yRef.value += spacingBefore;
  ensurePage(doc, yRef, requiredHeight);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(lineWidth);
  doc.line(MARGIN_X_MM, yRef.value, MARGIN_X_MM + CONTENT_WIDTH_MM, yRef.value);

  yRef.value += spacingAfter;
};

const cleanGroupTitle = (title) =>
  String(title ?? "")
    .replace(/=/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const writeGroupTitle = (doc, yRef, title, options = {}) => {
  const text = cleanGroupTitle(title) || "-";
  const spacingBefore = options.spacingBefore ?? 0.8;
  const spacingAfter = options.spacingAfter ?? 0.55;
  const lineGap = options.lineGap ?? 1.2;

  yRef.value += spacingBefore;

  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(GROUP_TITLE_FONT_SIZE);

  const naturalTitleWidth = doc.getTextWidth(text);
  const fontSize =
    naturalTitleWidth > CONTENT_WIDTH_MM
      ? Math.max(
          GROUP_TITLE_MIN_FONT_SIZE,
          GROUP_TITLE_FONT_SIZE * (CONTENT_WIDTH_MM / naturalTitleWidth)
        )
      : GROUP_TITLE_FONT_SIZE;
  const requiredHeight = spacingBefore + lineHeightForFont(fontSize);

  ensurePage(doc, yRef, requiredHeight);
  doc.setFontSize(fontSize);

  const centerX = MARGIN_X_MM + CONTENT_WIDTH_MM / 2;
  const titleWidth = doc.getTextWidth(text);
  const titleLeft = centerX - titleWidth / 2;
  const titleRight = centerX + titleWidth / 2;
  const lineY = yRef.value + lineHeightForFont(fontSize) / 2;
  const leftLineEnd = Math.max(MARGIN_X_MM, titleLeft - lineGap);
  const rightLineStart = Math.min(MARGIN_X_MM + CONTENT_WIDTH_MM, titleRight + lineGap);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  if (leftLineEnd > MARGIN_X_MM) {
    doc.line(MARGIN_X_MM, lineY, leftLineEnd, lineY);
  }
  if (rightLineStart < MARGIN_X_MM + CONTENT_WIDTH_MM) {
    doc.line(rightLineStart, lineY, MARGIN_X_MM + CONTENT_WIDTH_MM, lineY);
  }

  doc.text(text, centerX, yRef.value, { align: "center", baseline: "top" });
  yRef.value += lineHeightForFont(fontSize) + spacingAfter;
};

const writeTragaTableHeader = (doc, yRef) => {
  const spacingBefore = 0.2;
  const spacingAfter = 0.35;
  const requiredHeight = spacingBefore + lineHeightForFont(TRAGA_HEADER_FONT_SIZE);

  yRef.value += spacingBefore;
  ensurePage(doc, yRef, requiredHeight);

  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(TRAGA_HEADER_FONT_SIZE);
  doc.text("Entrada", TRAGA_ENTRY_RIGHT_X, yRef.value, { align: "right", baseline: "top" });
  doc.text("Salida", TRAGA_EXIT_RIGHT_X, yRef.value, { align: "right", baseline: "top" });

  yRef.value += lineHeightForFont(TRAGA_HEADER_FONT_SIZE) + spacingAfter;
};

const writeTragaTableRows = (doc, yRef, rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return;

  rows.forEach((row) => {
    const label = String(row?.label ?? "");
    const inValue = String(row?.inValue ?? "-");
    const outValue = String(row?.outValue ?? "-");
    const spacingAfter = 0.2;
    const requiredHeight = lineHeightForFont(TRAGA_VALUE_FONT_SIZE);

    ensurePage(doc, yRef, requiredHeight);
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(TRAGA_VALUE_FONT_SIZE);
    doc.text(label, TRAGA_LABEL_X, yRef.value, { align: "left", baseline: "top" });
    doc.text(inValue, TRAGA_ENTRY_RIGHT_X, yRef.value, { align: "right", baseline: "top" });
    doc.text(outValue, TRAGA_EXIT_RIGHT_X, yRef.value, { align: "right", baseline: "top" });

    yRef.value += lineHeightForFont(TRAGA_VALUE_FONT_SIZE) + spacingAfter;
  });
};

const writeTragaTotalLine = (doc, yRef, label, value) => {
  const labelText = String(label ?? "Total");
  const valueText = String(value ?? "-");
  const spacingAfter = 0.25;
  const fixedGap = 6;

  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(MACHINE_VALUE_FONT_SIZE);
  const naturalCombinedWidth =
    doc.getTextWidth(labelText) + fixedGap + doc.getTextWidth(valueText);
  const fontSize =
    naturalCombinedWidth > CONTENT_WIDTH_MM
      ? Math.max(8.8, MACHINE_VALUE_FONT_SIZE * (CONTENT_WIDTH_MM / naturalCombinedWidth))
      : MACHINE_VALUE_FONT_SIZE;

  doc.setFontSize(fontSize);
  const fittedCombinedWidth =
    doc.getTextWidth(labelText) + fixedGap + doc.getTextWidth(valueText);
  const horizontalScale =
    fittedCombinedWidth > CONTENT_WIDTH_MM ? CONTENT_WIDTH_MM / fittedCombinedWidth : 1;
  const valueX =
    MARGIN_X_MM + doc.getTextWidth(labelText) * horizontalScale + fixedGap;

  ensurePage(doc, yRef, lineHeightForFont(fontSize));
  doc.text(labelText, MARGIN_X_MM, yRef.value, {
    align: "left",
    baseline: "top",
    horizontalScale,
  });
  doc.text(valueText, valueX, yRef.value, {
    align: "left",
    baseline: "top",
    horizontalScale,
  });
  yRef.value += lineHeightForFont(fontSize) + spacingAfter;
};

const writeMachine = (doc, yRef, machine) => {
  writeLine(doc, yRef, machine.title, {
    fontSize: 9.6,
    fontStyle: "bold",
    spacingBefore: 1.2,
    spacingAfter: 0.5,
  });

  if (machine.configLine) {
    writeLine(doc, yRef, machine.configLine, {
      fontSize: 8.8,
      spacingAfter: 0.4,
    });
  }

  if (machine.status) {
    writeLine(doc, yRef, machine.status, {
      fontSize: 9,
      fontStyle: "bold",
      spacingAfter: 0.4,
    });
  }

  if (Array.isArray(machine.rows) && machine.rows.length > 0) {
    writeTragaTableRows(doc, yRef, machine.rows);
  }

  const valueFontSize =
    machine.type === "Peluchera" ? PELUCHERA_VALUE_FONT_SIZE : MACHINE_VALUE_FONT_SIZE;

  (machine.valueLines || []).forEach((line) => {
    const text = `${line.label} ${line.value ?? "-"}`;
    const options = { fontSize: valueFontSize, spacingAfter: 0.25 };
    if (machine.type === "Tragamoneda" && line.label === "Total") {
      writeTragaTotalLine(doc, yRef, line.label, line.value);
    } else if (machine.type === "Peluchera") {
      writePelucheraValueLine(doc, yRef, text, options);
    } else {
      writeLine(doc, yRef, text, options);
    }
  });

  (machine.resetLines || []).forEach((line, index) => {
    writePairLine(doc, yRef, line.label, line.value, {
      fontSize: 8.3,
      spacingBefore: index === 0 ? 0.4 : 0,
      spacingAfter: 0.2,
    });
  });

  (machine.noteLines || []).forEach((line, index) => {
    writeLine(doc, yRef, line, {
      fontSize: 8.3,
      spacingBefore: index === 0 ? 0.35 : 0,
      spacingAfter: 0.2,
    });
  });
};

const writeGroupSummary = (doc, yRef, summary) => {
  if (summary.percentageMode === "rented_by_type") {
    return;
  }

  if ((summary?.itemCount ?? 0) > 1) {
    writeFittedLine(doc, yRef, `Total ${summary.typeLabel} ${summary.totalAmountLabel}`, {
      fontSize: TYPE_TOTAL_FONT_SIZE,
      minFontSize: 8.8,
      fontStyle: "bold",
      spacingBefore: 0.8,
      spacingAfter: 0.2,
    });
  }

  const secondLine =
    summary.percentageMode === "single"
      ? `Cliente ${summary.clientPercentageLabel}% ${summary.clientAmountLabel} | Empresa ${summary.ownerPercentageLabel}% ${summary.ownerAmountLabel}`
      : `Cliente ${summary.clientAmountLabel} | Empresa ${summary.ownerAmountLabel}`;

  writeLine(doc, yRef, secondLine, {
    fontSize: 8.5,
    fontStyle: "bold",
    spacingBefore: (summary?.itemCount ?? 0) > 1 ? 0 : 0.8,
    spacingAfter: 0.15,
  });
};

const drawTicketContent = (doc, ticketModel, localOwnerLine) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_WIDTH_MM, pageHeight, "F");
  doc.setTextColor(0, 0, 0);

  const yRef = { value: MARGIN_TOP_MM };

  writeFittedLine(doc, yRef, localOwnerLine, {
    fontSize: TICKET_HEADER_TITLE_FONT_SIZE,
    minFontSize: 10,
    fontStyle: "bold",
    align: "center",
    wrapAtMin: true,
    spacingAfter: 0.3,
  });
  writeFittedLine(
    doc,
    yRef,
    `FECHA ${ticketModel?.header?.dateOnlyLabel ?? ticketModel?.header?.dateLabel ?? "-"}`,
    {
      fontSize: TICKET_HEADER_DATE_FONT_SIZE,
      minFontSize: 9.2,
      align: "center",
      wrapAtMin: true,
      spacingAfter: 0.3,
    }
  );

  (ticketModel?.machineGroups || []).forEach((group) => {
    writeGroupTitle(doc, yRef, group.title, {
      spacingBefore: 0.6,
      spacingAfter: 0.45,
    });

    if (
      group?.type === "Tragamoneda" &&
      (group.machines || []).some(
        (machine) => Array.isArray(machine?.rows) && machine.rows.length > 0
      )
    ) {
      writeTragaTableHeader(doc, yRef);
    }

    (group.machines || []).forEach((machine) => {
      writeMachine(doc, yRef, machine);
    });

    writeGroupSummary(doc, yRef, group.summary);
  });

  drawDividerLine(doc, yRef, {
    spacingBefore: 0.7,
    spacingAfter: 0.5,
  });

  writeFittedLine(doc, yRef, `TOTAL ${ticketModel?.generalTotals?.totalAmountLabel ?? "-"}`, {
    fontSize: GENERAL_TOTAL_FONT_SIZE,
    minFontSize: 10,
    fontStyle: "bold",
    align: "center",
    wrapAtMin: true,
    spacingBefore: 0.8,
    spacingAfter: 0.3,
  });
  writeFittedLine(
    doc,
    yRef,
    `Cliente ${ticketModel?.generalTotals?.clientAmountLabel ?? "-"} | Empresa ${ticketModel?.generalTotals?.ownerAmountLabel ?? "-"}`,
    {
      fontSize: GENERAL_SPLIT_TOTAL_FONT_SIZE,
      minFontSize: 8.8,
      fontStyle: "bold",
      align: "center",
      wrapAtMin: true,
      spacingAfter: 0.2,
    }
  );

  return yRef.value;
};

const drawPaperFeedAnchor = (doc, contentBottom) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const anchorX = MARGIN_X_MM + CONTENT_WIDTH_MM / 2;
  const anchorY = Math.min(
    contentBottom + FINAL_PAPER_FEED_MM,
    pageHeight - MARGIN_BOTTOM_MM
  );

  doc.setFillColor(0, 0, 0);
  doc.circle(anchorX, anchorY, PAPER_FEED_ANCHOR_RADIUS_MM, "F");
};

export default function generateCollectionTicketPdf(ticketModel = {}) {
  const filename = sanitizeFilename(
    `recaudacion-ticket-${ticketModel?.header?.visitId ?? "sin-id"}`
  );
  const headerLocal = String(ticketModel?.header?.localName ?? "").trim();
  const headerOwner = String(ticketModel?.header?.ownerName ?? "").trim();
  const localOwnerLine = [headerLocal, headerOwner].filter(Boolean).join(" | ") || "-";

  const measurementDoc = makeDoc();
  const contentBottom = drawTicketContent(measurementDoc, ticketModel, localOwnerLine);
  const pageHeight = Math.max(
    20,
    contentBottom + MARGIN_BOTTOM_MM + FINAL_PAPER_FEED_MM
  );
  const doc = makeDoc(pageHeight);
  drawTicketContent(doc, ticketModel, localOwnerLine);
  drawPaperFeedAnchor(doc, contentBottom);
  doc.save(`${filename}.pdf`);
  return true;
}
